import {
  FormFieldSchema,
  FormSchema
} from '../../../components/FormRenderer/types';
import {
  fontNames,
  initialSchoolConfigData,
  newRoleMapping,
  newSchool
} from './constants';
import { RoleGroups } from '../../../utils/roleAccess';

const featureConfigExtraValues: FormFieldSchema[] = Object.keys(
  initialSchoolConfigData.featureConfigs
).flatMap((key) => {
  const feature = (
    initialSchoolConfigData.featureConfigs as Record<string, any>
  )[key];

  // Determine role access for this feature
  const isSuperAdminFeature = [
    'postModeration',
    'studentRollover',
    'ensureId',
    'sendModerationMessagesToChatwoot'
  ].includes(key);

  const allowedRoles = isSuperAdminFeature
    ? RoleGroups.SUPER_ADMINS
    : RoleGroups.ADMINS;

  // base checkbox for "enabled"
  const children: FormFieldSchema[] = [
    {
      name: `featureConfigs.${key}.enabled`,
      label: key
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase()),
      type: 'checkbox',
      allowedRoles
    }
  ];

  // if feature has skipFor (array of emails)
  if (feature.skipFor) {
    children.push({
      name: `featureConfigs.${key}.skipFor`,
      label: `${key} Skip For`,
      type: 'multi-value-input',
      placeholder: 'Enter email',
      validationType: 'email',
      allowedRoles
    });
  }

  // if feature has metaData (object)
  if (feature.metaData) {
    Object.keys(feature.metaData).forEach((metaKey) => {
      if (key === 'ensureId') {
        children.push({
          name: `featureConfigs.${key}.metaData.${metaKey}`,
          label: `${key} ${metaKey}`,
          type: 'input',
          placeholder: `Enter ${metaKey}`,
          inputType: 'text',
          allowedRoles
        });
      } else {
        children.push({
          name: `featureConfigs.${key}.metaData.${metaKey}`,
          label: metaKey
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase()),
          type: 'checkbox',
          allowedRoles
        });
      }
    });
  }

  return children;
});

export const schoolConfigSchema: FormSchema = [
  {
    name: 'general',
    label: 'General',
    type: 'section',
    allowedRoles: RoleGroups.ALL_ADMINS,
    children: [
      {
        name: 'orgCode',
        label: 'Organization Code',
        type: 'input',
        placeholder: 'Organization Code',
        allowedRoles: RoleGroups.ALL_ADMINS
      },
      {
        name: 'studentEmaildDomain',
        label: 'Student Email Domain',
        type: 'input',
        placeholder: 'Student Email Domain',
        allowedRoles: RoleGroups.ALL_ADMINS
      },
      {
        name: 'timezoneDetails.timezone',
        label: 'Timezone',
        type: 'dropdown',
        options: [{ label: 'IST', value: 'IST' }],
        allowedRoles: RoleGroups.ALL_ADMINS
      },
      {
        name: 'logo',
        label: 'Organization Logo',
        type: 'fileUpload',
        title: 'Organization Logo',
        allowedRoles: RoleGroups.ALL_ADMINS,
        uploadFor: 'logo'
      }
    ]
  },
  {
    name: 'userCustomFieldMapping',
    label: 'Custom User Fields',
    type: 'section',
    allowedRoles: RoleGroups.SUPER_ADMINS,
    children: []
  },
  {
    name: 'schoolInfo',
    label: 'School Info',
    type: 'array',
    itemTemplate: newSchool,
    allowedRoles: RoleGroups.ALL_ADMINS,
    children: [
      {
        name: 'name',
        label: 'School Name',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'grades',
        label: 'Grades',
        type: 'multiSelect',
        options: [],
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'address',
        label: 'Address',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'emailIds',
        label: 'Email IDs',
        type: 'multi-value-input',
        placeholder: 'Type email and press enter',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'contacts',
        label: 'Contacts',
        type: 'multi-value-input',
        placeholder: 'Type contact and press enter',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'website',
        label: 'Website',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'udiseNo',
        label: 'UDISE No',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'affiliationNo',
        label: 'Affiliation No',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'branding',
        label: 'Branding',
        type: 'section',
        allowedRoles: RoleGroups.SUPER_ADMINS,
        children: [
          {
            name: 'studentIdCard',
            label: 'Student ID Card',
            type: 'section',
            allowedRoles: RoleGroups.SUPER_ADMINS,
            children: [
              {
                name: 'backgroundColor',
                label: 'Background Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'textColor',
                label: 'Text Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'fontName',
                label: 'Font Name',
                type: 'dropdown',
                options: fontNames,
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'logo',
                label: 'Logo',
                type: 'fileUpload',
                title: 'Student Id Logo',
                allowedRoles: RoleGroups.SUPER_ADMINS,
                uploadFor: 'IDCards'
              }
            ]
          },

          {
            name: 'parentIdCard',
            label: 'Parent ID Card',
            type: 'section',
            allowedRoles: RoleGroups.SUPER_ADMINS,
            children: [
              {
                name: 'backgroundColor',
                label: 'Background Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'textColor',
                label: 'Text Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'fontName',
                label: 'Font Name',
                type: 'dropdown',
                options: fontNames,
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'logo',
                label: 'Logo',
                type: 'fileUpload',
                title: 'Parent Id Card Logo',
                allowedRoles: RoleGroups.SUPER_ADMINS,
                uploadFor: 'IDCards'
              }
            ]
          },
          {
            name: 'guardianIdCard',
            label: 'Guardian ID Card',
            type: 'section',
            allowedRoles: RoleGroups.SUPER_ADMINS,
            children: [
              {
                name: 'backgroundColor',
                label: 'Background Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'textColor',
                label: 'Text Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'fontName',
                label: 'Font Name',
                type: 'dropdown',
                options: fontNames,
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'logo',
                label: 'Logo',
                type: 'fileUpload',
                title: 'Guardian Id Card Logo',
                allowedRoles: RoleGroups.SUPER_ADMINS,
                uploadFor: 'IDCards'
              }
            ]
          },
          {
            name: 'studentLC',
            label: 'Student LC Branding',
            type: 'section',
            allowedRoles: RoleGroups.SUPER_ADMINS,
            children: [
              {
                name: 'lcHeadingColor',
                label: 'LC Heading Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'pipeColor',
                label: 'Pipe Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'schoolNameColor',
                label: 'School Name Color',
                type: 'input',
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'fontName',
                label: 'Font Name',
                type: 'dropdown',
                options: fontNames,
                allowedRoles: RoleGroups.SUPER_ADMINS
              },
              {
                name: 'schoolLogo',
                label: 'School Logo',
                type: 'fileUpload',
                title: 'Student Lc Logo',
                allowedRoles: RoleGroups.SUPER_ADMINS,
                uploadFor: 'LC'
              },
              {
                name: 'watermark',
                label: 'Watermark',
                type: 'fileUpload',
                title: 'Student Lc Watermark',
                allowedRoles: RoleGroups.SUPER_ADMINS,
                uploadFor: 'LC'
              }
            ]
          },
          {
            name: 'dailyClassUpdate',
            label: 'Daily Class Update Branding',
            type: 'section',
            allowedRoles: RoleGroups.SUPER_ADMINS,

            children: [
              {
                name: 'schoolLogo',
                label: 'Logo',
                type: 'fileUpload',
                title: 'DCU Logo',
                allowedRoles: RoleGroups.SUPER_ADMINS,
                uploadFor: 'DCU'
              }
            ]
          }
        ]
      }
    ]
  },
  {
    name: 'featureConfigs',
    label: 'Feature Flags',
    type: 'section',
    allowedRoles: RoleGroups.ADMINS,
    children: featureConfigExtraValues
  },
  {
    name: 'welcomeEmailNotificationsConfig',
    label: 'Welcome Email Content',
    type: 'section',
    allowedRoles: RoleGroups.ADMINS,
    children: [
      {
        name: 'welcomeEmailNotificationsConfig.schoolFullName',
        label: 'School Full Name',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'welcomeEmailNotificationsConfig.brandName',
        label: 'Brand Name',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'welcomeEmailNotificationsConfig.knowlarityNumber',
        label: 'Knowlarity Number',
        type: 'input',
        gridProps: { sm: 4 },
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'welcomeEmailNotificationsConfig.fullFeeConcession',
        label: 'Full Fee Concession',
        type: 'input',
        gridProps: { sm: 4 },
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'welcomeEmailNotificationsConfig.admissionOfferValidityDays',
        label: 'Admission Offer Validity Days',
        type: 'input',
        gridProps: { sm: 4 },
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'welcomeEmailNotificationsConfig.brandSignOffLine',
        label: 'Brand Sign Off Line',
        type: 'input',
        gridProps: { sm: 12 },
        allowedRoles: RoleGroups.ADMINS
      }
    ]
  },
  {
    name: 'crmToEtherGradeMappings',
    label: 'Grade Mappings',
    type: 'section',
    allowedRoles: RoleGroups.ALL_ADMINS,
    children: []
  },
  {
    name: 'functionalUserEmails',
    label: 'Functional User Emails',
    type: 'section',
    allowedRoles: RoleGroups.ADMINS,
    children: [
      {
        name: 'functionalUserEmails.accountsEmail',
        label: 'Accounts Email',
        type: 'input',
        displayTitle: 'Account Email',
        displaySubtitle: '(This email will be used for payment related things)',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'functionalUserEmails.welcomeEmailId',
        label: 'Welcome Email ID',
        type: 'input',
        displayTitle: 'Welcome Email',
        displaySubtitle: '(This email will be used for sending welcome emails)',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'functionalUserEmails.grooveHqEmailId',
        label: 'Moderator Email',
        type: 'input',
        allowedRoles: RoleGroups.ADMINS,
        shouldRender: (formData) =>
          !formData.featureConfigs?.sendModerationMessagesToChatwoot?.enabled
      }
    ]
  },
  {
    name: 'parentsPostPublishAccess',
    label: 'Parent Post Publish Access',
    type: 'section',
    allowedRoles: RoleGroups.ADMINS,
    children: [
      {
        name: 'parentsPostPublishAccess.CLASS_TEACHERS.isEnabled',
        label: 'Class Teachers',
        type: 'checkbox',
        allowedRoles: RoleGroups.ADMINS
      },
      {
        name: 'parentsPostPublishAccess.INDIVIDUAL.isEnabled',
        label: 'Individual',
        type: 'checkbox',
        allowedRoles: RoleGroups.ADMINS
      }
      //   {
      //     name: 'parentsPostPublishAccess.INDIVIDUAL.allowedRoles',
      //     label: 'Allowed Roles',
      //     type: 'multiSelect',
      //     options: [],
      //     allowedRoles: RoleGroups.ADMINS
      //   }
    ]
  },
  {
    name: 'roleFeaturesMapping',
    label: 'Role Feature Mapping',
    type: 'array',
    itemTemplate: newRoleMapping,
    allowedRoles: RoleGroups.ADMINS,
    children: []
  }
];
